// Whether the composer strips known tracking IDs from links before it sends (#311, shared/src/trackingLinks.ts).
// Saved to the account like channel activity (/api/me/settings), and in localStorage so it applies before the
// account loads on this device. On by default.
import { useSyncExternalStore } from "react";
import { stripTracking } from "@bloom/shared";
import { api } from "./api.ts";

const KEY = "bloom:strip-tracking";
const listeners = new Set<() => void>();

function stored(): boolean {
  try {
    return localStorage.getItem(KEY) !== "off";
  } catch {
    return true;
  }
}

let on = stored();

/** Sets it on this device right away; `save` also stores it on the account for my other devices. */
export function setStripTracking(next: boolean, { save = true } = {}) {
  on = next;
  try {
    localStorage.setItem(KEY, next ? "on" : "off");
  } catch {
    // private mode: still applies for this page
  }
  for (const fn of listeners) fn();
  if (save) void api("/me/settings", { method: "PUT", body: { stripTracking: next } }).catch(() => {});
}

export const getStripTracking = () => on;

const subscribe = (fn: () => void) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

export const useStripTracking = (): boolean => useSyncExternalStore(subscribe, () => on);

/** The text to send: with the setting on, known tracking IDs are gone from its links. It never touches anyone else's text. */
export function textToSend(text: string): string {
  return on ? stripTracking(text) : text;
}
